import {
  Directive,
  ElementRef,
  inject,
  input,
  output,
  Renderer2
} from '@angular/core';

import { DEFAULT_SIZE_LIMIT } from '@file-upload/const';
import { Attachment } from '@file-upload/models/attachment';
import { FileValidationService } from '@file-upload/services/file-validation.service';

@Directive({
  selector: '[appFileUploadDrop]',
  standalone: true,
  host: {
    '(drop)': 'onDrop($event)',
    '(dragover)': 'onDragOver($event)',
    '(dragleave)': 'onDragLeave($event)'
  }
})
export class FileUploadDropDirective {
  readonly #renderer = inject(Renderer2);
  readonly #hostElement = inject(ElementRef);
  readonly #fileValidationService = inject(FileValidationService);

  readonly accept = input.required<string>();
  readonly multiple = input.required<boolean>();
  readonly files = input<Attachment[]>([]);
  readonly sizeLimit = input(DEFAULT_SIZE_LIMIT);
  readonly imageSizeLimit = input(DEFAULT_SIZE_LIMIT);

  readonly filesDrop = output<File[]>();

  readonly dragZoneActiveClass = 'drag-zone-active';
  readonly dragZoneMultipleError = 'drag-zone-multiple-error';

  onDrop($event: DragEvent): void {
    $event.preventDefault();
    $event.stopImmediatePropagation();

    this.removeClass(this.dragZoneActiveClass);
    this.removeClass(this.dragZoneMultipleError);

    if (this.canUploadMoreFiles($event.dataTransfer.files)) {
      this.onFilesDropped($event.dataTransfer.files);
    }
  }

  onDragOver($event: DragEvent): void {
    $event.preventDefault();
    $event.stopImmediatePropagation();

    this.addClass(this.dragZoneActiveClass);

    if (!this.canUploadMoreFiles($event.dataTransfer.items)) {
      this.addClass(this.dragZoneMultipleError);
    }
  }

  onDragLeave($event: DragEvent): void {
    $event.preventDefault();
    $event.stopImmediatePropagation();

    this.removeClass(this.dragZoneActiveClass);
    this.removeClass(this.dragZoneMultipleError);
  }

  canUploadMoreFiles(items: FileList | DataTransferItemList): boolean {
    // the sum of items to add and already added items must be equal or
    // lower than 1 if multiple uploads are not allowed
    return (
      this.multiple() || (items || []).length + (this.files() || []).length <= 1
    );
  }

  onFilesDropped(fileList: FileList): void {
    const files = Array.from(fileList || []);

    const validFiles = this.#fileValidationService.validateFiles(
      files,
      this.accept(),
      this.sizeLimit(),
      this.imageSizeLimit()
    );

    this.filesDrop.emit(validFiles);
  }

  addClass(cssClass: string): void {
    this.#renderer.addClass(this.#hostElement.nativeElement, cssClass);
  }

  removeClass(cssClass: string): void {
    this.#renderer.removeClass(this.#hostElement.nativeElement, cssClass);
  }
}
